import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fromPairs, get, isEmpty, map, pipe, values } from "lodash/fp";
import classNames from "classnames";

import Dropdown from "app/components/shared/Dropdown";
import Emojify from "app/components/shared/Emojify";
import Icon from "app/components/shared/Icon";
import Indicator from "app/components/shared/Indicator";
import cable from "app/lib/cable";
import { getMicrosecondDurationFormattedString } from "app/lib/date";
import { CommandStep } from "app/lib/pipeline";
import { track } from "app/lib/segmentAnalytics";
import { Current } from "app/utils/Current";

import { useTestEngineStore, TestPlan } from "./useTestEngineStore";

interface Props {
  buildId: string;
  steps: CommandStep[];
}

const testPlanResultSelector = pipe(get("testPlans"), values);

const BktecBadge = ({ buildId, steps }: Props) => {
  const setTestPlan = useTestEngineStore((state) => state.setTestPlan);

  const [opened, setOpened] = useState(false);
  const hasTestPlans = useTestEngineStore((state) => state.hasTestPlans);

  useEffect(() => {
    const subscription = cable.subscriptions.create(
      {
        channel: "TestEngine::TestPlanChannel",
        uuid: buildId,
        organization_uuid: Current.organization.uuid,
      },
      {
        received({ testPlan }) {
          setTestPlan(testPlan);
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [buildId, setTestPlan]);

  const handleClick = useCallback(() => {
    if (!opened) {
      // send segment event when badge is opened for the first time
      track("TE Bktec Badge Clicked", {
        pipeline_slug: Current.project.slug,
        pipeline_uuid: Current.project.uuid,
        organization_slug: Current.organization.slug,
        organization_uuid: Current.organization.uuid,
      });
    }
    setOpened(true);
  }, [opened]);

  return (
    <Dropdown width={300} className="inline-block" offsetY={5} nibOffsetX={22}>
      <button
        onClick={handleClick}
        className={classNames(
          "relative badge border rounded-lg flex gap-2 items-center font-mono [box-shadow:0_3px_0_0_var(--tw-shadow-color)] active:translate-y-0.5 active:[box-shadow:0_0.125rem_0_0_var(--tw-shadow-color)] transition-all duration-200",
          { "bg-slate-300 border-slate-500 shadow-slate-500": !hasTestPlans },
          {
            "bg-purple-100 border-purple-600 shadow-purple-600 ": hasTestPlans,
          },
        )}
      >
        bktec
        <Icon icon="down-triangle" style={{ width: 7, height: 7 }} />
        {hasTestPlans && !opened && (
          <span className="absolute left-full bottom-full -translate-x-1/2 translate-y-1/2">
            <Indicator />
          </span>
        )}
      </button>
      <div className="p-3 flex flex-col gap-3">
        <InformationalPanelContent steps={steps} />
      </div>
    </Dropdown>
  );
};

const MarketingPanelContent = () => <div>Marketing goes here</div>;

const InformationalPanelContent = ({ steps }: { steps: CommandStep[] }) => {
  const testPlans = useTestEngineStore(testPlanResultSelector);
  const hasTestPlans = useTestEngineStore((state) => state.hasTestPlans);

  const labels = useMemo(
    () =>
      pipe(
        map((step: CommandStep) => [
          step.uuid,
          step.label || step.commandScript,
        ]),
        fromPairs,
      )(steps),
    [steps],
  );

  return hasTestPlans ? (
    <>
      {map(
        (testPlan) => (
          <details
            key={testPlan.id}
            name="test-plan-result"
            className="flex flex-col font-mono bg-purple-100 border border-purple-600 rounded-lg shadow-purple-600 [box-shadow:0_3px_0_0_var(--tw-shadow-color)]"
          >
            <summary className="cursor-pointer p-3">
              <Emojify text={get(get("stepId", testPlan), labels)} />
            </summary>
            <div className="px-3">
              {isEmpty(testPlan.statistics.results) && (
                <div className="mb-2 text-xs">
                  <Icon
                    icon="heroicons/outline/exclamation-circle"
                    className="w-4 h-4"
                  />{" "}
                  Upgrade your bktec version to access detailed results.
                </div>
              )}
              <ResultTable testPlan={testPlan} />
            </div>
          </details>
        ),
        testPlans,
      )}
    </>
  ) : (
    <div className="font-mono text-xs">
      <b>bktec is enabled on this pipeline</b>
      <br />
      <b>---------------------------------</b>
      <br />
      Details will be displayed here for steps running bktec in this build.
      <br />
      <br />
      <a
        href="https://github.com/buildkite/test-engine-client"
        target="_blank"
        className="hover:text-underline"
        rel="noreferrer"
      >
        Learn more »
      </a>
    </div>
  );
};

const ResultTable = ({ testPlan }: { testPlan: TestPlan }) => {
  const result = testPlan.statistics.results;

  const totalDuration =
    testPlan.count * testPlan.statistics.mean_node_duration_ms;
  const timeSavedMs = totalDuration - testPlan.statistics.max_node_duration_ms;

  return (
    <div className="py-3 border-t border-purple-300">
      <table className="table-auto w-full leading-relaxed">
        <tbody>
          <tr>
            <td rowSpan={2} className="align-top">
              Passed
            </td>

            <td>on first run</td>

            <td className="text-right">{result?.passed_on_first_run || "-"}</td>
          </tr>

          <tr>
            <td>on retry</td>

            <td className="text-right">{result?.passed_on_retry || "-"}</td>
          </tr>

          <tr>
            <td rowSpan={2} className="align-top">
              Muted
            </td>

            <td>passed</td>

            <td className="text-right">{result?.muted_passed || "-"}</td>
          </tr>

          <tr>
            <td>failed</td>

            <td className="text-right">{result?.muted_failed || "-"}</td>
          </tr>

          <tr>
            <td colSpan={2}>Failed</td>

            <td className="text-right">{result?.failed || "-"}</td>
          </tr>

          <tr>
            <td className="pb-2" colSpan={2}>
              Skipped
            </td>

            <td className="pb-2 text-right">{result?.skipped || "-"}</td>
          </tr>

          <tr className="border-t border-purple-300 font-bold">
            <td className="pt-2" colSpan={2}>
              Total tests
            </td>

            <td className="pt-2 text-right">{result?.total || "-"}</td>
          </tr>

          <tr className="font-bold">
            <td className="pt-2" colSpan={2}>
              Time saved
            </td>

            <td className="pt-2 text-right">
              {timeSavedMs > 0 ? (
                <>
                  ~{getMicrosecondDurationFormattedString(timeSavedMs * 1000)}
                </>
              ) : (
                <>-</>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default BktecBadge;
